// Homepage fitting (sections/af-home-fitting.liquid). Back (Restart once done) sits in the card's heading row, as on
// the product page; once the fit is confirmed the Shop Classic / Shop Grand buttons show (and hide again on Restart);
// the card keeps one height while its steps change and once the fit is confirmed, the longer lists scrolling inside
// it (31 Oct 2026: it no longer eases down to its content when the fit is confirmed, nor back up on Restart);
// the card's glass leans a little towards the pointer. A background video, if one is set, plays through the site's one
// background-video player (assets/af-site.js, data-af-video). The fitting itself is snippets/fur-trim-quiz.liquid.

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const MAX_TILT = 1.6;

class AfHomeFitting extends HTMLElement {
  connectedCallback() {
    // The contact form works on its own too ("Contact form only")
    this.setUpContactForm();
    const card = this.querySelector('[data-af-hfit-card]');
    const fitting = card?.querySelector('.fur-quiz');
    if (!card || !fitting) return;

    const back = card.querySelector('.fur-quiz-container [data-af-back]');
    const heading = card.querySelector('.af-quiz-top');
    if (back && heading) heading.prepend(back);

    const shop = this.querySelector('[data-af-hfit-shop]');
    const sync = () => {
      const done = fitting.dataset.afFitReady === 'true';
      if (shop) shop.hidden = !done;
      this.classList.toggle('af-hfit--done', done);
      this.showSavedNote(fitting, done);
    };
    this.watcher = new MutationObserver(sync);
    this.watcher.observe(fitting, { attributes: true, attributeFilter: ['data-af-fit-ready', 'data-af-step'] });
    sync();
    this.setUpFixedHeight(card, fitting);

    if (reducedMotion() || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    card.addEventListener('pointermove', (event) => {
      const rect = card.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      card.style.setProperty('--af-tilt-x', `${(-y * MAX_TILT).toFixed(2)}deg`);
      card.style.setProperty('--af-tilt-y', `${(x * MAX_TILT).toFixed(2)}deg`);
    });
    card.addEventListener('pointerleave', () => {
      card.style.setProperty('--af-tilt-x', '0deg');
      card.style.setProperty('--af-tilt-y', '0deg');
    });
  }

  // Once the fit is confirmed: "Your fit is saved. Every trim is now matched to your {jacket}. Choose a cut below."
  // under the card's title (the section's settings), or the measured wording for a length the customer typed. Read
  // from the fitting's own memory (arcticfur:quiz-fit:v1), which it has written by the time this runs (the change is
  // noticed after the fitting's own code has finished).
  showSavedNote(fitting, done) {
    const title = fitting.querySelector('[data-af-confirm-title]');
    if (!title) return;
    let note = fitting.querySelector('.af-fit-confirm__note');
    if (!done) {
      if (note) note.hidden = true;
      return;
    }
    {
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem('arcticfur:quiz-fit:v1'));
      } catch {
        saved = null;
      }
      const length = saved?.needsLength ? String(saved.zipperLength || '').replace(/[^\d.]/g, '') : '';
      const jacket = fitting.dataset.afFitName || 'jacket';
      const template = length ? this.dataset.savedNoteMeasured : this.dataset.savedNote;
      if (!template) return;
      if (!note) {
        note = document.createElement('p');
        note.className = 'af-fit-confirm__note';
        title.after(note);
      }
      note.textContent = template.replace(/\{jacket\}/g, jacket).replace(/\{length\}/g, length);
      note.hidden = false;
    }
  }

  // The card keeps one height: the first question's own height, measured with the card at its natural size (never
  // taller than the screen less 6rem, af-design.css), kept once the fit is confirmed too. The longer jacket lists
  // scroll inside it, fading at the edge where there's more. A page that opens already fitted takes its height the
  // first time the first question shows.
  setUpFixedHeight(card, fitting) {
    const list = card.querySelector('.fur-quiz-container');
    if (!list) return;
    setUpScrollFade(list);
    const onFirstQuestion = () => !!fitting.querySelector('[data-af-step="q1"]:not(.hidden)')
      && !fitting.querySelector('[data-af-step="q1"] [aria-expanded="true"]')
      && fitting.dataset.afFitReady !== 'true';
    let frame = 0;
    let width = 0;
    const measure = () => {
      frame = 0;
      if (!onFirstQuestion()) return;
      card.classList.add('af-fit--measure');
      const natural = Math.ceil(card.getBoundingClientRect().height);
      card.classList.remove('af-fit--measure');
      card.style.setProperty('--af-fit-height', `${natural}px`);
      card.classList.add('af-fit--locked');
      this.style.removeProperty('min-height');
      this.style.minHeight = `${Math.ceil(this.getBoundingClientRect().height)}px`;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    // Each new step starts at the top of the list
    this.stepWatcher = new MutationObserver(() => {
      list.scrollTop = 0;
      schedule();
    });
    this.stepWatcher.observe(fitting, { attributes: true, attributeFilter: ['data-af-step'] });
    // A new width changes the first question's height, so it's measured again then, or the next time it shows
    this.sizeWatcher = new ResizeObserver(() => {
      const rect = card.getBoundingClientRect();
      if (Math.round(rect.width) === width) return;
      width = Math.round(rect.width);
      schedule();
    });
    this.sizeWatcher.observe(card);
    document.fonts?.ready.then(schedule);
    schedule();
  }

  // The contact layout's form: choosing "Other" for "Where did you hear about us?" opens its short field and puts the
  // cursor there; choosing anything else clears it, so only one answer is sent
  setUpContactForm() {
    const form = this.querySelector('.af-cform__form');
    const other = form?.querySelector('.af-cform__other');
    if (!form || !other) return;
    form.addEventListener('change', (event) => {
      if (event.target.name !== 'contact[Heard about us]') return;
      if (event.target.hasAttribute('data-af-other')) requestAnimationFrame(() => other.focus());
      else other.value = '';
    });
  }

  disconnectedCallback() {
    this.watcher?.disconnect();
    this.stepWatcher?.disconnect();
    this.sizeWatcher?.disconnect();
  }
}

// A list that scrolls inside a fixed frame fades out at the edge where there's more to see (.af-scroll-fade in
// af-pdp.css; the same as setUpScrollFade in af-pdp.js). Each fade follows how much is hidden past that edge.
function setUpScrollFade(list) {
  list.classList.add('af-scroll-fade');
  let frame = 0;
  const check = () => {
    frame = 0;
    const hidden = list.scrollHeight - list.clientHeight;
    const above = hidden > 2 ? Math.max(0, list.scrollTop) : 0;
    const below = hidden > 2 ? Math.max(0, hidden - list.scrollTop) : 0;
    const depth = (hiddenPx, least, most) => (hiddenPx > 2 ? Math.round(Math.min(most, Math.max(least, hiddenPx))) : 0);
    // Shallow fades (31 Oct 2026): deeper ones turned the beige answers grey
    list.style.setProperty('--af-fade-top', `${depth(above, 10, 16)}px`);
    list.style.setProperty('--af-fade-bottom', `${depth(below, 14, 28)}px`);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(check); };
  list.addEventListener('scroll', schedule, { passive: true });
  list.addEventListener('load', schedule, true);
  const sizes = new ResizeObserver(schedule);
  sizes.observe(list);
  [...list.children].forEach((child) => sizes.observe(child));
  new MutationObserver(schedule).observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'open'] });
  check();
}

if (!customElements.get('af-home-fitting')) customElements.define('af-home-fitting', AfHomeFitting);
